import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  Calendar as CalendarIcon,
  ArrowLeft,
  Send,
  AlertCircle,
  Loader2,
  Truck,
  Check,
  RefreshCw,
  X,
  Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { format, getDay, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const DAYS_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Options et labels pour les actions de rupture de stock
const OUT_OF_STOCK_OPTIONS = {
  replace_better: {
    label: 'Remplacement ≥',
    shortLabel: 'Remplacer',
    icon: RefreshCw,
    color: 'bg-green-100 text-green-700 border-green-200',
    description: 'Accepter un remplacement de qualité égale ou supérieure (avec alignement de prix)'
  },
  cancel: {
    label: 'Annuler',
    shortLabel: 'Annuler',
    icon: X,
    color: 'bg-red-100 text-red-700 border-red-200',
    description: 'Annuler l\'article si rupture de stock'
  },
  call_first: {
    label: 'Appeler',
    shortLabel: 'Appeler',
    icon: Phone,
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    description: 'Nous appeler avant de remplacer'
  }
};

const CartPage = () => {
  const { api } = useAuth();
  const { 
    getCartItems,
    getCartTotal, 
    getCartCount,
    updateQuantity, 
    removeFromCart, 
    clearCart,
    addToCart,
    getCartBySupplier,
    setSupplierDeliveryDate,
    getSupplierDeliveryDate,
    allDeliveryDatesSet,
    deliveryDates,
    addCustomProduct,
    removeCustomProduct,
    getCustomProductsForSupplier,
    setOutOfStockAction,
    setOutOfStockActionForAll
  } = useCart();
  const navigate = useNavigate();
  const [notes, setNotes] = useState({});  // { supplier_id: notes }
  const [loading, setLoading] = useState(false);
  const [openCalendars, setOpenCalendars] = useState({}); // { supplier_id: boolean }
  const [reliquats, setReliquats] = useState([]);

  useEffect(() => {
    api.get('/reliquats?status=pending')
      .then(({ data }) => setReliquats(data || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddReliquat = async (reliquat) => {
    try {
      const { data: product } = await api.get(`/products/${reliquat.product_id}`);
      addToCart(product, reliquat.quantity);
      await api.patch(`/reliquats/${reliquat.id}`, { status: 'added' });
      setReliquats(prev => prev.filter(r => r.id !== reliquat.id));
      toast.success(`${reliquat.article} ajouté au panier (reliquat)`);
    } catch {
      toast.error("Impossible d'ajouter ce reliquat");
    }
  };

  const handleDismissReliquat = async (reliquat) => {
    try {
      await api.patch(`/reliquats/${reliquat.id}`, { status: 'dismissed' });
      setReliquats(prev => prev.filter(r => r.id !== reliquat.id));
    } catch {
      toast.error('Erreur');
    }
  };

  const cartBySupplier = getCartBySupplier();
  const cartItems = getCartItems();

  const isDeliveryDay = (date, deliveryDays) => {
    const dayOfWeek = getDay(date);
    // Convert from JS day (0=Sunday) to our format (0=Monday)
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return deliveryDays.includes(adjustedDay);
  };

  // Calculate the minimum order date based on lead time and cutoff hour
  const getMinOrderDate = (leadTimeDays, cutoffHour) => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // Start from tomorrow
    let minDate = new Date(now);
    minDate.setDate(minDate.getDate() + leadTimeDays);
    
    // If we're past the cutoff hour today, add one more day
    if (currentHour >= cutoffHour) {
      minDate.setDate(minDate.getDate() + 1);
    }
    
    return startOfDay(minDate);
  };

  const disabledDays = (date, deliveryDays, leadTimeDays = 1, cutoffHour = 12) => {
    const minDate = getMinOrderDate(leadTimeDays, cutoffHour);
    return date < minDate || !isDeliveryDay(date, deliveryDays);
  };

  // Get first available delivery date for a supplier
  const getFirstAvailableDate = (deliveryDays, leadTimeDays = 1, cutoffHour = 12) => {
    const minDate = getMinOrderDate(leadTimeDays, cutoffHour);
    let checkDate = new Date(minDate);
    
    // Find the first delivery day starting from minDate
    for (let i = 0; i < 30; i++) {
      if (isDeliveryDay(checkDate, deliveryDays)) {
        return checkDate;
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }
    return minDate;
  };

  const setCalendarOpen = (supplierId, open) => {
    setOpenCalendars(prev => ({ ...prev, [supplierId]: open }));
  };

  const setSupplierNotes = (supplierId, value) => {
    setNotes(prev => ({ ...prev, [supplierId]: value }));
  };

  const handleSubmitOrders = async () => {
    // Check all delivery dates are set
    const missingDates = cartBySupplier.filter(s => !getSupplierDeliveryDate(s.supplier_id));
    if (missingDates.length > 0) {
      toast.error(`Veuillez sélectionner une date de livraison pour: ${missingDates.map(s => s.supplier_name).join(', ')}`);
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    setLoading(true);
    try {
      // Build multi-order payload with custom products per supplier
      const ordersData = {
        orders: cartBySupplier.map(supplierGroup => ({
          supplier_id: supplierGroup.supplier_id,
          delivery_date: format(getSupplierDeliveryDate(supplierGroup.supplier_id), 'yyyy-MM-dd'),
          items: supplierGroup.items.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity,
            out_of_stock_action: item.outOfStockAction || 'replace_better'
          })),
          notes: notes[supplierGroup.supplier_id] || '',
          custom_products: getCustomProductsForSupplier(supplierGroup.supplier_id).map(p => ({
            name: p.name,
            quantity: p.quantity
          }))
        }))
      };

      const response = await api.post('/orders/multi', ordersData);
      
      if (response.data.total_orders > 0) {
        toast.success(`${response.data.total_orders} commande(s) créée(s) avec succès`);
        clearCart();
        navigate('/orders');
      } else {
        toast.error('Aucune commande créée');
      }
    } catch (error) {
      console.error('Failed to create orders:', error);
      toast.error(formatErrorMessage(error, 'Erreur lors de la création des commandes'));
    } finally {
      setLoading(false);
    }
  };

  const renderOrphanReliquats = (list) => list.length > 0 && (
    <Card className="border border-amber-300 bg-amber-50/60" data-testid="orphan-reliquats-card">
      <CardContent className="p-4 space-y-2">
        <p className="text-sm font-semibold text-amber-800">
          ⏳ Reliquats attendus (non livrés sur de précédentes commandes) :
        </p>
        {list.map(r => (
          <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="flex-1 min-w-[200px]">
              <span className="font-mono text-xs bg-white px-1 rounded border mr-1">{r.code}</span>
              {r.article} — <strong>{r.quantity}</strong>
              <span className="text-gray-500"> • {r.supplier_name} (cmd {r.from_order_number})</span>
            </span>
            <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-800" onClick={() => handleAddReliquat(r)} data-testid={`add-reliquat-${r.id}`}>
              + Ajouter au panier
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-gray-500" onClick={() => handleDismissReliquat(r)}>
              Ignorer
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  if (cartItems.length === 0) {
    return (
      <div className="space-y-6 animate-slide-in" data-testid="cart-page">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/catalog')}
            className="mb-2 -ml-2 text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au catalogue
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Panier</h1>
        </div>
        
        <Card className="border border-gray-200">
          <CardContent className="py-12 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Votre panier est vide</h3>
            <p className="text-gray-500 mb-4">Ajoutez des produits depuis le catalogue</p>
            <Button 
              onClick={() => navigate('/catalog')} 
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
            >
              Voir le catalogue
            </Button>
          </CardContent>
        </Card>
        {renderOrphanReliquats(reliquats)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="cart-page">
      <div>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/catalog')}
          className="mb-2 -ml-2 text-gray-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au catalogue
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Panier</h1>
        <p className="text-gray-500 mt-1">
          {getCartCount()} articles • {cartBySupplier.length} fournisseur{cartBySupplier.length > 1 ? 's' : ''}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Reliquats de fournisseurs absents du panier */}
          {renderOrphanReliquats(reliquats.filter(r => !cartBySupplier.some(g => g.supplier_id === r.supplier_id)))}
          {/* Cart items grouped by supplier */}
          {cartBySupplier.map((supplierGroup) => {
            const deliveryDate = getSupplierDeliveryDate(supplierGroup.supplier_id);
            const isCalendarOpen = openCalendars[supplierGroup.supplier_id] || false;
            
            return (
              <Card 
                key={supplierGroup.supplier_id} 
                className="border border-gray-200"
                data-testid={`supplier-section-${supplierGroup.supplier_id}`}
              >
                <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Truck className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{supplierGroup.supplier_name}</CardTitle>
                        <p className="text-sm text-gray-500">
                          {supplierGroup.items.length} article{supplierGroup.items.length > 1 ? 's' : ''} • {supplierGroup.total.toFixed(2)} €
                        </p>
                      </div>
                    </div>
                    
                    {/* Delivery date selector for this supplier */}
                    <Popover open={isCalendarOpen} onOpenChange={(open) => setCalendarOpen(supplierGroup.supplier_id, open)}>
                      <PopoverTrigger asChild>
                        <Button 
                          variant={deliveryDate ? "outline" : "default"}
                          className={deliveryDate 
                            ? "border-green-500 text-green-700 bg-green-50 hover:bg-green-100" 
                            : "bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                          }
                          data-testid={`delivery-date-btn-${supplierGroup.supplier_id}`}
                        >
                          {deliveryDate ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              {format(deliveryDate, 'd MMM', { locale: fr })}
                            </>
                          ) : (
                            <>
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              Date livraison
                            </>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={deliveryDate}
                          onSelect={(date) => {
                            setSupplierDeliveryDate(supplierGroup.supplier_id, date);
                            setCalendarOpen(supplierGroup.supplier_id, false);
                          }}
                          disabled={(date) => disabledDays(
                            date, 
                            supplierGroup.supplier_delivery_days,
                            supplierGroup.supplier_lead_time_days || 1,
                            supplierGroup.supplier_cutoff_hour || 12
                          )}
                          locale={fr}
                          initialFocus
                          defaultMonth={getFirstAvailableDate(
                            supplierGroup.supplier_delivery_days,
                            supplierGroup.supplier_lead_time_days || 1,
                            supplierGroup.supplier_cutoff_hour || 12
                          )}
                        />
                        <div className="p-3 border-t bg-gray-50 space-y-1">
                          <p className="text-xs text-gray-500">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            Livraisons: {supplierGroup.supplier_delivery_days.map(d => DAYS_LABELS[d]).join(', ')}
                          </p>
                          <p className="text-xs text-gray-400">
                            Commander {supplierGroup.supplier_lead_time_days || 1} jour(s) avant, jusqu'à {supplierGroup.supplier_cutoff_hour || 12}h
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </CardHeader>
                
                <CardContent className="p-0">
                  {/* Reliquats attendus de ce fournisseur */}
                  {reliquats.filter(r => r.supplier_id === supplierGroup.supplier_id).length > 0 && (
                    <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 space-y-2" data-testid={`reliquats-banner-${supplierGroup.supplier_id}`}>
                      <p className="text-sm font-semibold text-amber-800">
                        ⏳ Reliquats attendus de ce fournisseur (non livrés précédemment) :
                      </p>
                      {reliquats.filter(r => r.supplier_id === supplierGroup.supplier_id).map(r => (
                        <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="flex-1 min-w-[180px]">
                            <span className="font-mono text-xs bg-white px-1 rounded border mr-1">{r.code}</span>
                            {r.article} — <strong>{r.quantity}</strong> (cmd {r.from_order_number})
                          </span>
                          <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-800" onClick={() => handleAddReliquat(r)} data-testid={`add-reliquat-${r.id}`}>
                            + Ajouter
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-gray-500" onClick={() => handleDismissReliquat(r)}>
                            Ignorer
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Out of stock action group selector */}
                  <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">En cas de rupture :</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        defaultValue="replace_better"
                        onValueChange={(action) => {
                          // Appliquer à tous les produits de ce fournisseur
                          supplierGroup.items.forEach(item => {
                            setOutOfStockAction(item.product.id, action);
                          });
                          toast.success(`Action appliquée à ${supplierGroup.items.length} article(s)`);
                        }}
                      >
                        <SelectTrigger className="w-[200px] h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(OUT_OF_STOCK_OPTIONS).map(([key, option]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <option.icon className="w-4 h-4" />
                                {option.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          supplierGroup.items.forEach(item => {
                            setOutOfStockAction(item.product.id, 'replace_better');
                          });
                          toast.success('Action réinitialisée pour tous les articles');
                        }}
                      >
                        Réinitialiser
                      </Button>
                    </div>
                  </div>
                  
                  {/* Products list */}
                  <div className="divide-y">
                    {supplierGroup.items.map((item) => (
                      <div 
                        key={item.product.id} 
                        className="p-4 flex items-center justify-between hover:bg-gray-50"
                        data-testid={`cart-item-${item.product.code}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {item.product.code}
                            </span>
                            {/* Badge action rupture */}
                            {item.outOfStockAction && OUT_OF_STOCK_OPTIONS[item.outOfStockAction] && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Badge 
                                    variant="outline" 
                                    className={`cursor-pointer hover:opacity-80 transition-opacity ${OUT_OF_STOCK_OPTIONS[item.outOfStockAction].color}`}
                                  >
                                    {React.createElement(OUT_OF_STOCK_OPTIONS[item.outOfStockAction].icon, {
                                      className: "w-3 h-3 mr-1"
                                    })}
                                    {OUT_OF_STOCK_OPTIONS[item.outOfStockAction].shortLabel}
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent className="w-80" align="start">
                                  <div className="space-y-3">
                                    <div>
                                      <h4 className="font-semibold text-sm mb-1">Action en cas de rupture</h4>
                                      <p className="text-xs text-gray-500">
                                        {OUT_OF_STOCK_OPTIONS[item.outOfStockAction].description}
                                      </p>
                                    </div>
                                    <div className="space-y-2">
                                      {Object.entries(OUT_OF_STOCK_OPTIONS).map(([key, option]) => (
                                        <button
                                          key={key}
                                          onClick={() => setOutOfStockAction(item.product.id, key)}
                                          className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                                            item.outOfStockAction === key 
                                              ? option.color + ' border-current' 
                                              : 'border-gray-200 hover:bg-gray-50'
                                          }`}
                                        >
                                          <option.icon className="w-4 h-4" />
                                          <div className="flex-1 text-left">
                                            <p className="text-sm font-medium">{option.label}</p>
                                            <p className="text-xs text-gray-500">{option.description}</p>
                                          </div>
                                          {item.outOfStockAction === key && (
                                            <Check className="w-4 h-4" />
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900">{item.product.article}</h4>
                          
                          {/* Affichage conditionnement : nouveau format ou legacy */}
                          {item.product.supplier_packaging_quantity && item.product.supplier_packaging_unit ? (
                            <div className="text-xs text-gray-500 space-y-1 mt-1">
                              <p>
                                Vendu: {item.product.supplier_packaging_quantity}×{item.product.supplier_packaging_unit} par {item.product.supplier_packaging_type || 'unité'}
                              </p>
                              {item.product.is_divisible ? (
                                <p className="font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded inline-block">
                                  ✓ Détaillable par {item.product.divisible_quantity || 1} {item.product.divisible_unit}
                                </p>
                              ) : (
                                <p className="text-gray-400 italic">Non détaillable</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">{item.product.min_packaging} • {item.product.unit}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="icon" 
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              data-testid={`cart-decrease-${item.product.code}-btn`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-10 text-center font-semibold tabular-nums">
                              {item.quantity}
                            </span>
                            <Button 
                              size="icon" 
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              data-testid={`cart-increase-${item.product.code}-btn`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeFromCart(item.product.id)}
                            data-testid={`cart-remove-${item.product.code}-btn`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Custom Products (Non-referenced) for this supplier */}
                  <div className="p-4 border-t bg-gray-50">
                    <div className="flex items-center gap-2 mb-3">
                      <Plus className="w-4 h-4 text-gray-500" />
                      <Label className="text-sm font-medium text-gray-700">
                        Ajouter un produit hors catalogue
                      </Label>
                    </div>
                    
                    {/* List of custom products for this supplier */}
                    {getCustomProductsForSupplier(supplierGroup.supplier_id).length > 0 && (
                      <div className="space-y-2 mb-3">
                        {getCustomProductsForSupplier(supplierGroup.supplier_id).map((product) => (
                          <div 
                            key={product.id} 
                            className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                            data-testid={`custom-product-${product.id}`}
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-700">{product.name}</p>
                              <p className="text-xs text-gray-500">Qté : {product.quantity}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                removeCustomProduct(supplierGroup.supplier_id, product.id);
                                toast.success('Produit supprimé');
                              }}
                              className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add custom product form */}
                    <div className="grid grid-cols-5 gap-2">
                      <Input
                        id={`custom-product-name-${supplierGroup.supplier_id}`}
                        placeholder="Nom du produit"
                        className="h-9 text-sm col-span-2 bg-white border-gray-300"
                        data-testid={`custom-product-name-input-${supplierGroup.supplier_id}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            document.getElementById(`custom-product-quantity-${supplierGroup.supplier_id}`)?.focus();
                          }
                        }}
                      />
                      <Input
                        id={`custom-product-quantity-${supplierGroup.supplier_id}`}
                        placeholder="Quantité"
                        className="h-9 text-sm col-span-2 bg-white border-gray-300"
                        data-testid={`custom-product-quantity-input-${supplierGroup.supplier_id}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const btn = document.getElementById(`add-custom-product-btn-${supplierGroup.supplier_id}`);
                            btn?.click();
                          }
                        }}
                      />
                      <Button
                        id={`add-custom-product-btn-${supplierGroup.supplier_id}`}
                        onClick={() => {
                          const nameInput = document.getElementById(`custom-product-name-${supplierGroup.supplier_id}`);
                          const quantityInput = document.getElementById(`custom-product-quantity-${supplierGroup.supplier_id}`);
                          const name = nameInput?.value.trim();
                          const quantity = quantityInput?.value.trim();

                          if (!name || !quantity) {
                            toast.error('Remplissez le nom et la quantité');
                            return;
                          }

                          addCustomProduct(supplierGroup.supplier_id, name, quantity);
                          nameInput.value = '';
                          quantityInput.value = '';
                          nameInput?.focus();
                          toast.success('Produit ajouté');
                        }}
                        size="sm"
                        className="bg-gray-600 hover:bg-gray-700 text-white h-9"
                        data-testid={`add-custom-product-btn-${supplierGroup.supplier_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Notes for this supplier */}
                  <div className="p-4 border-t bg-gray-50">
                    <Label htmlFor={`notes-${supplierGroup.supplier_id}`} className="text-sm text-gray-600">
                      Notes pour {supplierGroup.supplier_name} (optionnel)
                    </Label>
                    <Textarea
                      id={`notes-${supplierGroup.supplier_id}`}
                      placeholder="Instructions particulières..."
                      value={notes[supplierGroup.supplier_id] || ''}
                      onChange={(e) => setSupplierNotes(supplierGroup.supplier_id, e.target.value)}
                      className="mt-2 min-h-[60px] bg-white"
                      data-testid={`order-notes-${supplierGroup.supplier_id}`}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Summary sidebar */}
        <div className="space-y-4">
          <Card className="border border-gray-200 sticky top-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Récapitulatif</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary per supplier */}
              <div className="space-y-3">
                {cartBySupplier.map((supplierGroup) => {
                  const deliveryDate = getSupplierDeliveryDate(supplierGroup.supplier_id);
                  return (
                    <div 
                      key={supplierGroup.supplier_id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-sm">{supplierGroup.supplier_name}</p>
                        <p className="text-xs text-gray-500">
                          {supplierGroup.items.length} article{supplierGroup.items.length > 1 ? 's' : ''}
                          {deliveryDate && (
                            <span className="ml-2 text-green-600">
                              • {format(deliveryDate, 'd MMM', { locale: fr })}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {deliveryDate ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total articles</span>
                  <span className="font-medium">{getCartCount()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Commandes à créer</span>
                  <span className="font-medium">{cartBySupplier.length}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total général</span>
                  <span className="tabular-nums">{getCartTotal().toFixed(2)} €</span>
                </div>
              </div>

              <Button 
                className="w-full bg-[#1B4332] hover:bg-[#0D2E1F] text-white h-12"
                onClick={handleSubmitOrders}
                disabled={loading || !allDeliveryDatesSet}
                data-testid="submit-order-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Créer {cartBySupplier.length} commande{cartBySupplier.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>

              {!allDeliveryDatesSet && (
                <p className="text-xs text-center text-orange-600">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Sélectionnez une date de livraison pour chaque fournisseur
                </p>
              )}

              <Button 
                variant="ghost" 
                className="w-full text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  clearCart();
                  toast.info('Panier vidé');
                }}
                data-testid="clear-cart-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Vider le panier
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
